/* ============================================================
   app.jsx — Main App: Leaflet ⇄ React state, drawing, selection
   ============================================================ */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import * as turf from '@turf/turf';

import { PG, buildLanePolygon, sampleWaypoints } from './geometry.js';
import { createEmptyScenario, DEFAULT_SETTINGS } from './data.js';
import {
  downloadJson, importJsonFile,
  downloadPdf, snapshotMapPng, downloadPng,
  cropToBbox, overlayWaypoints,
} from './export.js';
import {
  Topbar, Leftbar, HUD, LayersCard, StatusBar, DrawHint, Compass,
  Inspector, WelcomeOverlay,
} from './components.jsx';
import { Icon } from './ui-primitives.jsx';
import {
  SettingsModal, PdfExportModal, AutofillModal, AddressModal,
} from './modals.jsx';
import {
  useTweaks, TweaksPanel, TweakSection, TweakSlider, TweakToggle, TweakRadio,
} from './tweaks-panel.jsx';

// --------- Esri World Imagery tile layer (no API key) -----------
const ESRI_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const ESRI_ATTRIB = 'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics';

// --------- Map object styles -----------------------------------
const STYLES = {
  boundary: {
    color: '#ffd166',
    weight: 2,
    fillColor: '#ffd166',
    fillOpacity: 0.04,
    dashArray: '6 4',
  },
  boundarySelected: {
    color: '#f5d491',
    weight: 3,
    fillColor: '#ffd166',
    fillOpacity: 0.08,
  },
  lane: {
    color: '#9a8bff',
    fillColor: '#9a8bff',
    fillOpacity: 0.18,
    weight: 1.3,
    dashArray: '3 4',
  },
  laneSelected: {
    color: '#c4b9ff',
    fillColor: '#9a8bff',
    fillOpacity: 0.28,
    weight: 2,
  },
  obstacle: {
    color: '#ff7a5c',
    weight: 1.8,
    fillColor: '#ff7a5c',
    fillOpacity: 0.18,
  },
  obstacleSelected: {
    color: '#ff9a7c',
    weight: 2.5,
    fillColor: '#ff7a5c',
    fillOpacity: 0.28,
  },
  rowCenter: {
    color: '#6ea8ff',
    weight: 2,
    opacity: 0.9,
    dashArray: '6 3',
  },
  rowCenterSelected: {
    color: '#a8c7ff',
    weight: 3,
    opacity: 1,
  },
  slot: {
    color: '#e0d0bc',
    weight: 0.8,
    fillColor: '#bea78e',
    fillOpacity: 0.45,
  },
  slotAccessible: {
    color: '#a8d5ec',
    weight: 1,
    fillColor: '#5fb0d9',
    fillOpacity: 0.5,
  },
  slotBlocked: {
    color: '#ff8b85',
    weight: 1,
    fillColor: '#d9534f',
    fillOpacity: 0.35,
    dashArray: '3 2',
  },
  drawTemp: {
    color: '#f5d491',
    weight: 2,
    dashArray: '5 4',
    fillColor: '#bea78e',
    fillOpacity: 0.08,
  },
};

// =============================================================
// App
// =============================================================
// Default view when nothing has been picked yet: roughly centered on Germany.
const DEFAULT_VIEW = { center: [10.4515, 51.1657], zoom: 5 };

// Stub used by code paths that expect a scenario shape when none is loaded.
const EMPTY_SCENARIO = {
  id: '__empty__',
  name: '— kein Standort —',
  subtitle: 'Adresse eingeben, um zu starten',
  address: '',
  center: DEFAULT_VIEW.center,
  zoom: DEFAULT_VIEW.zoom,
  target: 0,
  boundaries: [], rows: [], lanes: [], obstacles: [], entries: [],
  settings: DEFAULT_SETTINGS,
};

const STORAGE_KEY = 'parking-manager:v1';

// ---- Coordinate-inspect popup -------------------------------------------
// Opens a Leaflet popup at the clicked point with lat/lng in decimal + DMS,
// plus a small "kopieren" button. The button is wired up after openOn() runs.
function toDms(value, posChar, negChar) {
  const sign = value < 0 ? negChar : posChar;
  const abs = Math.abs(value);
  const deg = Math.floor(abs);
  const minFloat = (abs - deg) * 60;
  const min = Math.floor(minFloat);
  const sec = (minFloat - min) * 60;
  return `${deg}° ${String(min).padStart(2, '0')}′ ${sec.toFixed(2).padStart(5, '0')}″ ${sign}`;
}

function openCoordsPopup(map, latlng) {
  const lat = latlng.lat;
  const lng = latlng.lng;
  const decimal = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  const dms = `${toDms(lat, 'N', 'S')}  ${toDms(lng, 'E', 'W')}`;

  const html = `
    <div style="font-family:var(--font-sans);min-width:200px;">
      <div style="font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:var(--text-3);margin-bottom:6px;">Koordinaten</div>
      <div style="font-family:var(--font-mono);font-size:12px;color:var(--text);margin-bottom:2px;">${decimal}</div>
      <div style="font-family:var(--font-mono);font-size:11px;color:var(--text-2);margin-bottom:8px;">${dms}</div>
      <button type="button" data-coord-copy="${decimal}"
              style="width:100%;height:24px;padding:0 8px;border:1px solid var(--line);background:var(--panel-2);color:var(--text);border-radius:4px;font-family:var(--font-sans);font-size:11.5px;cursor:pointer;">
        kopieren
      </button>
    </div>`;

  const popup = L.popup({ closeButton: true, autoClose: false, className: 'coords-popup', maxWidth: 280 })
    .setLatLng(latlng)
    .setContent(html)
    .openOn(map);

  // Wire the copy button after the popup is rendered into the DOM.
  setTimeout(() => {
    const root = popup.getElement();
    const btn = root?.querySelector('[data-coord-copy]');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(btn.getAttribute('data-coord-copy'));
        btn.textContent = 'kopiert ✓';
        btn.style.color = 'var(--success)';
      } catch {
        btn.textContent = 'Kopieren fehlgeschlagen';
        btn.style.color = 'var(--danger)';
      }
    });
  }, 0);
}

function loadPersisted() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (e) {
    console.warn('Failed to load persisted state:', e);
    return null;
  }
}

export default function App() {
  // --- Persistence: load once on first render. ---
  const persisted = useRef(loadPersisted()).current;

  // --- Scenarios. Hydrate from localStorage if present, otherwise start empty. ---
  const [scenarios, setScenarios] = useState(() => persisted?.scenarios || {});
  const [scenarioId, setScenarioId] = useState(() => {
    const id = persisted?.scenarioId;
    return id && persisted?.scenarios?.[id] ? id : null;
  });
  const scenario = scenarioId && scenarios[scenarioId] ? scenarios[scenarioId] : EMPTY_SCENARIO;
  const hasScenario = !!scenarioId && !!scenarios[scenarioId];

  // --- Undo/redo history. Keyed per scenario so switching tabs preserves each
  // scenario's own stack. Only mutateScenario writes here — non-geometry state
  // (tool, selection, modal, layer toggles) does NOT participate. ---
  const [history, setHistory] = useState({}); // { [scenarioId]: { past: [], future: [] } }
  const HISTORY_LIMIT = 50;
  const canUndo = !!(scenarioId && history[scenarioId]?.past?.length);
  const canRedo = !!(scenarioId && history[scenarioId]?.future?.length);

  // --- Current tool & selection ---
  const [tool, setTool] = useState('select');
  const [selection, setSelection] = useState(null); // { type:'row'|'lane'|'boundary'|'obstacle', id }
  const [drawing, setDrawing] = useState(null); // { type, points: [[lng,lat],...] }

  // --- Layer visibility ---
  const [layers, setLayers] = useState(() => ({
    satellite: true,
    boundary:  true,
    slots:     true,
    lanes:     true,
    obstacles: true,
    labels:    true,
    entries:   true,
    coords:    false,
    ...(persisted?.layers || {}),
  }));

  // --- Settings are now stored ON each scenario (persists + travels with
  // JSON export/import). `lastSettings` is a separate global default used to
  // pre-fill the settings step when creating a new scenario, so the user's
  // last choices carry over without being tied to any one project. ---
  const [lastSettings, setLastSettings] = useState(() => ({
    ...DEFAULT_SETTINGS,
    ...(persisted?.lastSettings || persisted?.settings || {}),
  }));
  const settings = scenario.settings || DEFAULT_SETTINGS;

  // --- Open modal --- (auto-open address picker only if no persisted scenario)
  const hasPersistedScenario = !!(persisted?.scenarioId && persisted?.scenarios?.[persisted.scenarioId]);
  const [modal, setModal] = useState(
    hasPersistedScenario ? null : { kind: 'address', mode: 'create' },
  );

  // --- Mouse coords + zoom for status bar ---
  const [mouseCoord, setMouseCoord] = useState(null);
  const [mapZoom, setMapZoom] = useState(DEFAULT_VIEW.zoom);

  // --- Hidden file input + transient import error message ---
  const fileInputRef = useRef(null);
  const [importError, setImportError] = useState(null);

  // --- Two-step new-project flow ---
  // After the user picks an address, we stash it here and open the settings
  // step. Only on confirm do we actually call createScenarioFromAddress.
  const [pendingAddress, setPendingAddress] = useState(null);   // { lat, lng, name, address, zoom } | null
  const [pendingSettings, setPendingSettings] = useState(null); // settings draft used by the create-mode SettingsModal

  // --- Tweaks (host protocol) ---
  const [tweaks, setTweak] = useTweaks(/*EDITMODE-BEGIN*/{
    "globalAngle": 90,
    "applyToAllRows": false,
    "doubleSided": false,
    "slotWidth": 2.5,
    "slotLength": 5.0,
    "target": 240,
    "showLabels": true,
    "highlightBlocked": true,
    "showAngleArrow": true
  }/*EDITMODE-END*/);

  // --- Map ref ---
  const mapRef = useRef(null);
  const mapEl  = useRef(null);
  const layersRef = useRef({}); // id -> Leaflet layer
  const tileRef = useRef(null);

  // --- Bridge between Leaflet's imperative event handlers and React state.
  // Leaflet click handlers are bound once when the map mounts, so they would
  // otherwise capture stale state. We sync `.current` on every render. ---
  const bridgeRef = useRef({
    tool: 'select',
    drawing: null,
    setDrawing: () => {},
    finishLine: () => {},
    finishPolygon: () => {},
    placeEntry: () => {},
  });

  // Transient "click two points to define a bearing" session, used by the
  // AutofillModal. When non-null, the map click handler routes here instead
  // of the active tool, captures up to 2 points, then resolves.
  const bearingPickRef = useRef(null); // { points: [[lng,lat]?], onDone: (bearingDeg)=>void } | null
  const [bearingPickActive, setBearingPickActive] = useState(false);

  // ----------------------------------------------------------------
  // Compute slots (memoised)
  // ----------------------------------------------------------------
  const allSlots = useMemo(() => {
    const obs = scenario.obstacles
      .map(o => { try { return turf.polygon([o.polygon]); } catch { return null; } })
      .filter(Boolean);

    const slots = [];
    for (const row of scenario.rows) {
      // For Tweaks "apply-to-all", override angle / sided / dims
      const effAngle = tweaks.applyToAllRows ? tweaks.globalAngle : row.angle;
      const effSide  = tweaks.applyToAllRows ? (tweaks.doubleSided ? 'both' : 'right') : row.side;
      const effW     = tweaks.applyToAllRows ? tweaks.slotWidth  : row.slotWidth;
      const effL     = tweaks.applyToAllRows ? tweaks.slotLength : row.slotLength;

      const generated = PG.generateSlotsForRow(
        { id: row.id, start: row.start, end: row.end, label: row.label },
        {
          slotWidth: effW,
          slotLength: effL,
          angle: effAngle,
          side: effSide,
          slantSign: row.slantSign,
          facing: row.facing,
          rowLabel: row.label,
          accessibleEvery: row.accessibleEvery || 0,
          accessibleWidth: settings.accessibleWidth,
          obstacles: obs,
        }
      );
      slots.push(...generated);
    }
    return slots;
  }, [scenario, tweaks.applyToAllRows, tweaks.globalAngle, tweaks.doubleSided, tweaks.slotWidth, tweaks.slotLength, settings.accessibleWidth]);

  const target = tweaks.target;
  const blockedCount = allSlots.filter(s => s.blocked).length;
  const counts = {
    total:  allSlots.length,
    rows:   scenario.rows.length,
  };

  // ----------------------------------------------------------------
  // Init Leaflet map (once)
  // ----------------------------------------------------------------
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const startCenter = hasScenario ? scenario.center : DEFAULT_VIEW.center;
    const startZoom   = hasScenario ? scenario.zoom   : DEFAULT_VIEW.zoom;
    const map = L.map(mapEl.current, {
      center: [startCenter[1], startCenter[0]],
      zoom: startZoom,
      zoomControl: true,
      attributionControl: true,
      preferCanvas: true,
      zoomSnap: 0.5,
      // Allow zooming past Esri's native cap. Leaflet up-samples the z19
      // tiles above maxNativeZoom — blurry but precise enough for placing
      // individual slots in close-up.
      maxZoom: 21,
    });
    L.control.zoom({ position: 'bottomright' }).remove();
    map.zoomControl.setPosition('bottomright');

    tileRef.current = L.tileLayer(ESRI_URL, {
      attribution: ESRI_ATTRIB,
      maxNativeZoom: 19,
      maxZoom: 21,
      detectRetina: true,
      // CORS so html2canvas can read the canvas for PNG/PDF snapshots.
      crossOrigin: true,
    }).addTo(map);

    mapRef.current = map;

    // mouse events for status bar
    map.on('mousemove', (e) => setMouseCoord(e.latlng));
    map.on('zoomend',   () => setMapZoom(map.getZoom()));

    // -----------------------------------------------------------
    // Click handler — drives drawing + selection
    // -----------------------------------------------------------
    map.on('click', (e) => {
      const ll = [e.latlng.lng, e.latlng.lat];

      // Bearing-pick session takes priority over normal tools.
      const pick = bearingPickRef.current;
      if (pick) {
        pick.points.push(ll);
        if (pick.points.length === 2) {
          const [p1, p2] = pick.points;
          const deg = ((PG.bearingOf(p1, p2) % 360) + 360) % 360;
          bearingPickRef.current = null;
          setBearingPickActive(false);
          pick.onDone(deg);
        }
        return;
      }

      const b = bridgeRef.current;
      const toolNow = b.tool;
      const drawingNow = b.drawing;

      if (toolNow === 'select' || toolNow === 'pan') return;

      if (toolNow === 'boundary' || toolNow === 'obstacle') {
        const next = drawingNow ? { ...drawingNow, points: [...drawingNow.points, ll] }
                                : { type: toolNow, points: [ll] };
        b.setDrawing(next);
        return;
      }

      if (toolNow === 'row' || toolNow === 'lane' || toolNow === 'measure') {
        if (!drawingNow || drawingNow.points.length === 0) {
          b.setDrawing({ type: toolNow, points: [ll] });
        } else if (drawingNow.points.length === 1) {
          b.finishLine([drawingNow.points[0], ll], toolNow);
        }
        return;
      }

      if (toolNow === 'entry') {
        b.placeEntry(ll);
        return;
      }

      if (toolNow === 'coords') {
        openCoordsPopup(map, e.latlng);
        return;
      }
    });

    map.on('dblclick', (e) => {
      const drawingNow = bridgeRef.current.drawing;
      if (drawingNow && (drawingNow.type === 'boundary' || drawingNow.type === 'obstacle') && drawingNow.points.length >= 3) {
        L.DomEvent.preventDefault(e);
        bridgeRef.current.finishPolygon();
      }
    });

    // Suppress double-click zoom while drawing polygons
    map.doubleClickZoom.disable();

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line
  }, []);

  // Sync the bridge ref every render so Leaflet's once-bound click handler
  // always sees the current React state/handlers (avoids effect dependency
  // hell on map.on/off).
  useEffect(() => {
    bridgeRef.current.tool = tool;
    bridgeRef.current.drawing = drawing;
    bridgeRef.current.setDrawing = setDrawing;
    bridgeRef.current.finishLine = handleFinishLine;
    bridgeRef.current.finishPolygon = handleFinishPolygon;
    bridgeRef.current.placeEntry = handlePlaceEntry;
  });

  // ----------------------------------------------------------------
  // Graticule (lat/lng grid) — toggled by the `coords` layer in Ebenen.
  // Owns its own L.LayerGroup so it's isolated from the scenario layers
  // wipe-and-redraw pass.
  // ----------------------------------------------------------------
  const graticuleRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    if (!layers.coords) {
      if (graticuleRef.current) {
        map.removeLayer(graticuleRef.current);
        graticuleRef.current = null;
      }
      return undefined;
    }

    const group = L.layerGroup().addTo(map);
    graticuleRef.current = group;

    // Pick a grid interval (degrees) appropriate for the current zoom.
    // 1° at the equator is ~111 km; at zoom 18 we want sub-second precision.
    function intervalForZoom(z) {
      if (z >= 18) return 0.0005;
      if (z >= 16) return 0.001;
      if (z >= 14) return 0.005;
      if (z >= 12) return 0.01;
      if (z >= 10) return 0.05;
      if (z >= 8)  return 0.1;
      if (z >= 6)  return 0.5;
      return 1;
    }

    function fmtCoord(v, step) {
      const decimals = step < 0.001 ? 4 : step < 0.01 ? 3 : step < 0.1 ? 2 : 1;
      return v.toFixed(decimals) + '°';
    }

    function redraw() {
      group.clearLayers();
      const z = map.getZoom();
      const step = intervalForZoom(z);
      const b = map.getBounds();

      const lineStyle = {
        color: '#a4a09a',
        weight: 0.6,
        opacity: 0.45,
        interactive: false,
        dashArray: '2 3',
      };

      const south = Math.floor(b.getSouth() / step) * step;
      const north = Math.ceil(b.getNorth() / step) * step;
      for (let lat = south; lat <= north + 1e-9; lat += step) {
        L.polyline([[lat, b.getWest()], [lat, b.getEast()]], lineStyle).addTo(group);
        L.marker([lat, b.getWest()], {
          interactive: false,
          icon: L.divIcon({
            className: 'graticule-label',
            html: fmtCoord(lat, step),
            iconSize: null,
            iconAnchor: [-4, 7],
          }),
        }).addTo(group);
      }

      const west = Math.floor(b.getWest() / step) * step;
      const east = Math.ceil(b.getEast() / step) * step;
      for (let lng = west; lng <= east + 1e-9; lng += step) {
        L.polyline([[b.getSouth(), lng], [b.getNorth(), lng]], lineStyle).addTo(group);
        L.marker([b.getNorth(), lng], {
          interactive: false,
          icon: L.divIcon({
            className: 'graticule-label',
            html: fmtCoord(lng, step),
            iconSize: null,
            iconAnchor: [-4, -2],
          }),
        }).addTo(group);
      }
    }

    redraw();
    map.on('moveend zoomend', redraw);

    return () => {
      map.off('moveend zoomend', redraw);
      if (graticuleRef.current) {
        map.removeLayer(graticuleRef.current);
        graticuleRef.current = null;
      }
    };
  }, [layers.coords]);

  // ----------------------------------------------------------------
  // Render scenario objects + slots into Leaflet layers
  // ----------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Wipe previous
    Object.values(layersRef.current).forEach(lyr => {
      try { map.removeLayer(lyr); } catch (e) {}
    });
    layersRef.current = {};

    // Tile layer visibility
    if (tileRef.current) {
      if (layers.satellite) {
        if (!map.hasLayer(tileRef.current)) tileRef.current.addTo(map);
        tileRef.current.setOpacity(1);
      } else {
        tileRef.current.setOpacity(0.05);
      }
    }

    // Only existing geometry is clickable while the Select tool is active.
    // Otherwise clicks pass straight through to the map so the user can draw
    // new geometry on top of (or inside) existing boundaries, lanes, and
    // obstacles without being blocked.
    const isSelectMode = tool === 'select';

    // Boundaries
    if (layers.boundary) {
      scenario.boundaries.forEach(b => {
        const isSel = selection?.type === 'boundary' && selection.id === b.id;
        const latlngs = b.polygon.map(([lng, lat]) => [lat, lng]);
        const poly = L.polygon(latlngs, {
          ...(isSel ? STYLES.boundarySelected : STYLES.boundary),
          interactive: isSelectMode,
        }).addTo(map);
        if (isSelectMode) {
          poly.on('click', (e) => { L.DomEvent.stopPropagation(e); selectIfPointer('boundary', b.id); });
        }
        layersRef.current['b-' + b.id] = poly;
      });
    }

    // Lanes
    if (layers.lanes) {
      scenario.lanes.forEach(lane => {
        const isSel = selection?.type === 'lane' && selection.id === lane.id;
        const lanePoly = PG.buildLanePolygon(lane);
        if (lanePoly) {
          const latlngs = lanePoly.map(([lng, lat]) => [lat, lng]);
          const poly = L.polygon(latlngs, {
            ...(isSel ? STYLES.laneSelected : STYLES.lane),
            interactive: isSelectMode,
          }).addTo(map);
          if (isSelectMode) {
            poly.on('click', (e) => { L.DomEvent.stopPropagation(e); selectIfPointer('lane', lane.id); });
          }
          layersRef.current['lane-' + lane.id] = poly;
        }
      });
    }

    // Obstacles
    if (layers.obstacles) {
      scenario.obstacles.forEach(o => {
        const isSel = selection?.type === 'obstacle' && selection.id === o.id;
        const latlngs = o.polygon.map(([lng, lat]) => [lat, lng]);
        const poly = L.polygon(latlngs, {
          ...(isSel ? STYLES.obstacleSelected : STYLES.obstacle),
          interactive: isSelectMode,
        }).addTo(map);
        if (isSelectMode) {
          poly.on('click', (e) => { L.DomEvent.stopPropagation(e); selectIfPointer('obstacle', o.id); });
        }
        layersRef.current['o-' + o.id] = poly;
      });
    }

    // Slots — always non-interactive; clicking a slot under Select selects the row via the row centerline.
    if (layers.slots) {
      allSlots.forEach(s => {
        const latlngs = s.polygon.map(([lng, lat]) => [lat, lng]);
        let style;
        if (s.blocked && tweaks.highlightBlocked) style = STYLES.slotBlocked;
        else if (s.accessible) style = STYLES.slotAccessible;
        else style = STYLES.slot;
        const poly = L.polygon(latlngs, { ...style, interactive: false }).addTo(map);
        layersRef.current['s-' + s.id] = poly;
      });
    }

    // Row centerlines + labels
    scenario.rows.forEach(r => {
      const isSel = selection?.type === 'row' && selection.id === r.id;
      const latlngs = [[r.start[1], r.start[0]], [r.end[1], r.end[0]]];
      const line = L.polyline(latlngs, {
        ...(isSel ? STYLES.rowCenterSelected : STYLES.rowCenter),
        interactive: isSelectMode,
      }).addTo(map);
      if (isSelectMode) {
        line.on('click', (e) => { L.DomEvent.stopPropagation(e); selectIfPointer('row', r.id); });
      }
      layersRef.current['r-' + r.id] = line;

      // Row label at row start
      if (layers.labels) {
        const labelDiv = L.divIcon({
          className: 'row-label',
          html: r.label,
          iconSize: null,
          iconAnchor: [-10, 8],
        });
        const labelMarker = L.marker([r.start[1], r.start[0]], { icon: labelDiv, interactive: false }).addTo(map);
        layersRef.current['rl-' + r.id] = labelMarker;
      }
    });

    // Entry / exit markers — draggable so the user can re-position them.
    // Only draggable in Select mode; non-interactive otherwise so they don't
    // swallow draw clicks.
    if (layers.entries) {
      // Combined entry/exit marker — the brief decided one Ein-/Ausfahrt
      // marker covers both directions, so all markers render identically.
      (scenario.entries || []).forEach(en => {
        const color = '#6b9a4a';
        const arrow = '↔';
        const icon = L.divIcon({
          className: 'entry-marker',
          html: `
            <div style="background:${color};color:#fff;border-radius:999px;padding:4px 9px;font-family:var(--font-mono);font-size:11px;font-weight:600;display:inline-flex;align-items:center;gap:6px;box-shadow:0 2px 8px rgba(0,0,0,0.5);border:1px solid rgba(255,255,255,0.3);cursor:${isSelectMode ? 'grab' : 'default'};">
              <span style="font-size:13px;line-height:1;">${arrow}</span>
              ${en.label}
            </div>`,
          iconSize: null,
          iconAnchor: [40, 14],
        });
        const m = L.marker([en.position[1], en.position[0]], {
          icon,
          interactive: isSelectMode,
          draggable: isSelectMode,
          autoPan: true,
        }).addTo(map);
        if (isSelectMode) {
          m.on('dragend', () => {
            const ll = m.getLatLng();
            mutateScenario(s => {
              const idx = (s.entries || []).findIndex(x => x.id === en.id);
              if (idx >= 0) s.entries[idx] = { ...s.entries[idx], position: [ll.lng, ll.lat] };
            });
          });
        }
        layersRef.current['en-' + en.id] = m;
      });
    }

    // ---------- Vertex handles for the SELECTED object (Select mode only) ----------
    if (isSelectMode && selection) {
      const handleStyle = {
        radius: 6,
        color: '#1a1a1c',
        weight: 1.5,
        fillColor: '#f5d491',
        fillOpacity: 1,
        interactive: true,
        bubblingMouseEvents: false,
      };

      const makeVertexHandle = (lng, lat, onDrag, onEnd) => {
        const h = L.circleMarker([lat, lng], handleStyle).addTo(map);
        // CircleMarker isn't natively draggable — drive it via map mousemove
        // while the pointer is down on the handle.
        h.on('mousedown', (downEvt) => {
          L.DomEvent.stopPropagation(downEvt);
          map.dragging.disable();
          const onMove = (mvEvt) => {
            onDrag(mvEvt.latlng.lng, mvEvt.latlng.lat);
          };
          const onUp = (upEvt) => {
            map.off('mousemove', onMove);
            map.off('mouseup', onUp);
            map.dragging.enable();
            onEnd(upEvt.latlng.lng, upEvt.latlng.lat);
          };
          map.on('mousemove', onMove);
          map.on('mouseup', onUp);
        });
        return h;
      };

      if (selection.type === 'boundary') {
        const b = scenario.boundaries.find(x => x.id === selection.id);
        if (b) {
          // polygon points are stored as a closed ring (last === first); skip the duplicate
          const ring = b.polygon.slice(0, -1);
          ring.forEach((pt, i) => {
            const poly = layersRef.current['b-' + b.id];
            const h = makeVertexHandle(
              pt[0], pt[1],
              (lng, lat) => {
                // live preview by rewriting layer latlngs without touching state
                if (!poly) return;
                const next = ring.map((p, j) => j === i ? [lat, lng] : [p[1], p[0]]);
                poly.setLatLngs(next);
              },
              (lng, lat) => {
                mutateScenario(s => {
                  const idx = s.boundaries.findIndex(x => x.id === b.id);
                  if (idx < 0) return;
                  const nextRing = s.boundaries[idx].polygon.slice(0, -1).map((p, j) => j === i ? [lng, lat] : p);
                  s.boundaries[idx].polygon = [...nextRing, nextRing[0]];
                });
              }
            );
            layersRef.current['vh-b-' + b.id + '-' + i] = h;
          });
        }
      } else if (selection.type === 'obstacle') {
        const o = scenario.obstacles.find(x => x.id === selection.id);
        if (o) {
          const ring = o.polygon.slice(0, -1);
          ring.forEach((pt, i) => {
            const poly = layersRef.current['o-' + o.id];
            const h = makeVertexHandle(
              pt[0], pt[1],
              (lng, lat) => {
                if (!poly) return;
                const next = ring.map((p, j) => j === i ? [lat, lng] : [p[1], p[0]]);
                poly.setLatLngs(next);
              },
              (lng, lat) => {
                mutateScenario(s => {
                  const idx = s.obstacles.findIndex(x => x.id === o.id);
                  if (idx < 0) return;
                  const nextRing = s.obstacles[idx].polygon.slice(0, -1).map((p, j) => j === i ? [lng, lat] : p);
                  s.obstacles[idx].polygon = [...nextRing, nextRing[0]];
                });
              }
            );
            layersRef.current['vh-o-' + o.id + '-' + i] = h;
          });
        }
      } else if (selection.type === 'row') {
        const r = scenario.rows.find(x => x.id === selection.id);
        if (r) {
          const line = layersRef.current['r-' + r.id];
          const endpoints = [
            { key: 'start', pt: r.start },
            { key: 'end',   pt: r.end },
          ];
          endpoints.forEach(({ key, pt }, i) => {
            const h = makeVertexHandle(
              pt[0], pt[1],
              (lng, lat) => {
                if (!line) return;
                const sLL = [r.start[1], r.start[0]];
                const eLL = [r.end[1], r.end[0]];
                if (key === 'start') line.setLatLngs([[lat, lng], eLL]);
                else                 line.setLatLngs([sLL, [lat, lng]]);
              },
              (lng, lat) => {
                mutateScenario(s => {
                  const idx = s.rows.findIndex(x => x.id === r.id);
                  if (idx < 0) return;
                  s.rows[idx] = { ...s.rows[idx], [key]: [lng, lat] };
                });
              }
            );
            layersRef.current['vh-r-' + r.id + '-' + i] = h;
          });
        }
      } else if (selection.type === 'lane') {
        const lane = scenario.lanes.find(x => x.id === selection.id);
        if (lane) {
          const endpoints = [
            { key: 'start', pt: lane.start },
            { key: 'end',   pt: lane.end },
          ];
          endpoints.forEach(({ key, pt }, i) => {
            const h = makeVertexHandle(
              pt[0], pt[1],
              () => { /* lane polygon is recomputed from start/end via buildLanePolygon — defer to commit */ },
              (lng, lat) => {
                mutateScenario(s => {
                  const idx = s.lanes.findIndex(x => x.id === lane.id);
                  if (idx < 0) return;
                  s.lanes[idx] = { ...s.lanes[idx], [key]: [lng, lat] };
                });
              }
            );
            layersRef.current['vh-lane-' + lane.id + '-' + i] = h;
          });
        }
      }
    }

    // ---------- Drawing preview ----------
    if (drawing) {
      if (drawing.type === 'boundary' || drawing.type === 'obstacle') {
        if (drawing.points.length === 1) {
          // single dot
          const dot = L.circleMarker([drawing.points[0][1], drawing.points[0][0]],
            { radius: 4, color: '#f5d491', fillColor: '#f5d491', fillOpacity: 1, weight: 2 }).addTo(map);
          layersRef.current['_draw-pt'] = dot;
        } else {
          const latlngs = drawing.points.map(([lng, lat]) => [lat, lng]);
          const poly = drawing.points.length >= 3
            ? L.polygon(latlngs, STYLES.drawTemp).addTo(map)
            : L.polyline(latlngs, STYLES.drawTemp).addTo(map);
          layersRef.current['_draw-poly'] = poly;
          drawing.points.forEach((p, i) => {
            const dot = L.circleMarker([p[1], p[0]],
              { radius: 3, color: '#f5d491', fillColor: '#f5d491', fillOpacity: 1, weight: 1.5 }).addTo(map);
            layersRef.current['_draw-pt-' + i] = dot;
          });
        }
      } else if (drawing.type === 'row' || drawing.type === 'lane' || drawing.type === 'measure') {
        if (drawing.points.length >= 1) {
          const dot = L.circleMarker([drawing.points[0][1], drawing.points[0][0]],
            { radius: 4, color: '#f5d491', fillColor: '#f5d491', fillOpacity: 1, weight: 2 }).addTo(map);
          layersRef.current['_draw-pt'] = dot;
        }
      }
    }
  }, [scenario, selection, layers, allSlots, drawing, tweaks.highlightBlocked, tool]);

  // ----------------------------------------------------------------
  // Scenario switching: fly to the new scenario
  // ----------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!hasScenario) return;
    map.flyTo([scenario.center[1], scenario.center[0]], scenario.zoom, { duration: 0.6 });
    setSelection(null);
    setDrawing(null);
    setTweak({ target: scenario.target || 0 });
    // eslint-disable-next-line
  }, [scenarioId]);

  // ----------------------------------------------------------------
  // Persistence — write to localStorage whenever durable state changes.
  // Debounced so vertex-drag streams don't thrash. Skips transient state
  // (tool, selection, drawing, modal, history) by design.
  // ----------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handle = setTimeout(() => {
      try {
        const payload = JSON.stringify({ scenarios, scenarioId, layers, lastSettings });
        window.localStorage.setItem(STORAGE_KEY, payload);
      } catch (e) {
        // Quota or serialization issue — log once and keep going.
        console.warn('Failed to persist state:', e);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [scenarios, scenarioId, layers, lastSettings]);

  // ----------------------------------------------------------------
  // Selection helper — only select when in select tool
  // ----------------------------------------------------------------
  function selectIfPointer(type, id) {
    if (bridgeRef.current.tool !== 'select') return;
    setSelection({ type, id });
  }

  // ----------------------------------------------------------------
  // Drawing finalization handlers
  // ----------------------------------------------------------------
  function handleFinishLine(pts, kind) {
    setDrawing(null);
    if (kind === 'measure') {
      const dist = PG.distanceOf(pts[0], pts[1]);
      const map = mapRef.current;
      const mid = L.latLng((pts[0][1] + pts[1][1]) / 2, (pts[0][0] + pts[1][0]) / 2);
      const tip = L.popup({
        closeButton: true, autoClose: false, className: 'measure-popup',
      })
      .setLatLng(mid)
      .setContent(`<div style="font-family:var(--font-mono);font-size:13px;color:#1a1a1c;font-weight:600;">${dist.toFixed(1)} m</div>`)
      .openOn(map);
      // Optional persistent line
      const line = L.polyline([[pts[0][1], pts[0][0]], [pts[1][1], pts[1][0]]],
        { color: '#f5d491', weight: 2, dashArray: '4 3' }).addTo(map);
      setTimeout(() => { try { map.removeLayer(line); } catch (e) {} }, 4500);
      return;
    }

    if (kind === 'row') {
      const newRow = {
        id: 'row-' + Date.now(),
        type: 'row',
        label: nextRowLabel(),
        start: pts[0],
        end: pts[1],
        angle: settings.defaultAngle,
        slotWidth: settings.slotWidth,
        slotLength: settings.slotLength,
        side: settings.defaultSide,
        slantSign: 1,
        facing: 'in',
        doubleSided: settings.defaultSide === 'both',
        accessibleEvery: 0,
      };
      mutateScenario(s => { s.rows.push(newRow); });
      setSelection({ type: 'row', id: newRow.id });
      setTool('select');
    } else if (kind === 'lane') {
      const newLane = {
        id: 'lane-' + Date.now(),
        type: 'lane',
        start: pts[0],
        end: pts[1],
        width: settings.laneWidthTwoWay,
        direction: 'two-way',
      };
      mutateScenario(s => { s.lanes.push(newLane); });
      setSelection({ type: 'lane', id: newLane.id });
      setTool('select');
    }
  }

  function handleFinishPolygon() {
    const d = bridgeRef.current.drawing;
    if (!d || d.points.length < 3) return;
    const pts = [...d.points, d.points[0]];
    setDrawing(null);
    if (d.type === 'boundary') {
      const newB = {
        id: 'b-' + Date.now(),
        type: 'boundary',
        label: 'Neue Fläche',
        polygon: pts,
      };
      mutateScenario(s => { s.boundaries.push(newB); });
      setSelection({ type: 'boundary', id: newB.id });
      setTool('select');
    } else if (d.type === 'obstacle') {
      const newO = {
        id: 'o-' + Date.now(),
        type: 'obstacle',
        label: 'Hindernis',
        kind: 'custom',
        polygon: pts,
      };
      mutateScenario(s => { s.obstacles.push(newO); });
      setSelection({ type: 'obstacle', id: newO.id });
      setTool('select');
    }
  }

  function handlePlaceEntry(ll) {
    const newE = {
      id: 'en-' + Date.now(),
      type: 'entry',
      label: 'Ein/Aus',
      position: ll,
    };
    mutateScenario(s => { s.entries = s.entries || []; s.entries.push(newE); });
    setTool('select');
  }

  function nextRowLabel() {
    const used = new Set(scenario.rows.map(r => r.label.replace(/['*"]/g, '')));
    for (let i = 0; i < 26; i++) {
      const ch = String.fromCharCode(65 + i);
      if (!used.has(ch)) return ch;
    }
    return 'Z';
  }

  // ----------------------------------------------------------------
  // Scenario creation / location change from address picker
  // ----------------------------------------------------------------
  function createScenarioFromAddress({ lat, lng, name, address, zoom, settings: initSettings }) {
    const sc = createEmptyScenario({
      name: name || 'Neuer Standort',
      subtitle: address || '',
      address: address || '',
      lat, lng,
      zoom: zoom || 18,
      target: 0,
      settings: initSettings,
    });
    setScenarios((prev) => ({ ...prev, [sc.id]: sc }));
    setHistory((h) => ({ ...h, [sc.id]: { past: [], future: [] } }));
    setScenarioId(sc.id);
  }

  function changeScenarioAddress({ lat, lng, name, address, zoom }) {
    if (!hasScenario) return;
    mutateScenario((s) => {
      s.center = [lng, lat];
      s.zoom   = zoom || s.zoom;
      s.name   = name || s.name;
      s.address = address || '';
      s.subtitle = address || s.subtitle;
    });
    const map = mapRef.current;
    if (map) map.flyTo([lat, lng], zoom || 18, { duration: 0.6 });
  }

  function deleteCurrentScenario() {
    if (!scenarioId) return;
    const remaining = { ...scenarios };
    delete remaining[scenarioId];
    setScenarios(remaining);
    setHistory((h) => {
      const next = { ...h };
      delete next[scenarioId];
      return next;
    });
    const nextId = Object.keys(remaining)[0] || null;
    setScenarioId(nextId);
    if (!nextId) setModal({ kind: 'address', mode: 'create' });
  }

  // ----------------------------------------------------------------
  // Mutation helpers (immutable update of scenarios)
  // ----------------------------------------------------------------
  function mutateScenario(fn) {
    if (!scenarioId) return;
    setScenarios(prev => {
      const current = prev[scenarioId];
      if (!current) return prev;
      // Record the pre-mutation scenario in history (capped at HISTORY_LIMIT).
      setHistory(h => {
        const entry = h[scenarioId] || { past: [], future: [] };
        const past = [...entry.past, current];
        if (past.length > HISTORY_LIMIT) past.shift();
        return { ...h, [scenarioId]: { past, future: [] } };
      });
      const copy = JSON.parse(JSON.stringify(current));
      fn(copy);
      return { ...prev, [scenarioId]: copy };
    });
  }

  function undo() {
    if (!scenarioId) return;
    const entry = history[scenarioId];
    if (!entry || !entry.past.length) return;
    const current = scenarios[scenarioId];
    const prevState = entry.past[entry.past.length - 1];
    setScenarios(s => ({ ...s, [scenarioId]: prevState }));
    setHistory(h => ({
      ...h,
      [scenarioId]: {
        past: entry.past.slice(0, -1),
        future: current ? [...entry.future, current] : entry.future,
      },
    }));
    setSelection(null);
    setDrawing(null);
  }

  function redo() {
    if (!scenarioId) return;
    const entry = history[scenarioId];
    if (!entry || !entry.future.length) return;
    const current = scenarios[scenarioId];
    const nextState = entry.future[entry.future.length - 1];
    setScenarios(s => ({ ...s, [scenarioId]: nextState }));
    setHistory(h => ({
      ...h,
      [scenarioId]: {
        past: current ? [...entry.past, current] : entry.past,
        future: entry.future.slice(0, -1),
      },
    }));
    setSelection(null);
    setDrawing(null);
  }

  function updateRow(id, patch, kind = 'row') {
    mutateScenario(s => {
      const arr = kind === 'row' ? s.rows : kind === 'lane' ? s.lanes : null;
      if (!arr) return;
      const idx = arr.findIndex(x => x.id === id);
      if (idx >= 0) arr[idx] = { ...arr[idx], ...patch };
    });
  }

  function deleteObject(type, id) {
    mutateScenario(s => {
      if (type === 'row')      s.rows      = s.rows.filter(x => x.id !== id);
      if (type === 'lane')     s.lanes     = s.lanes.filter(x => x.id !== id);
      if (type === 'boundary') s.boundaries = s.boundaries.filter(x => x.id !== id);
      if (type === 'obstacle') s.obstacles = s.obstacles.filter(x => x.id !== id);
    });
    setSelection(null);
  }

  function addRowsToBoundary(boundaryId) {
    const b = scenario.boundaries.find(x => x.id === boundaryId);
    if (!b) return;
    setModal({ kind: 'autofill', boundaryId });
  }

  function applyAutofill({ angle, slot, doubleSided, accessibleEvery, bearing, direction }, boundaryId) {
    const b = scenario.boundaries.find(x => x.id === (boundaryId || scenario.boundaries[0]?.id));
    if (!b) return;
    const obs = scenario.obstacles.map(o => { try { return turf.polygon([o.polygon]); } catch { return null; } }).filter(Boolean);
    const out = PG.autoFillPolygon(b, {
      slotWidth: slot.w,
      slotLength: slot.L,
      angle,
      doubleSided,
      accessibleEvery,
      bearing: bearing != null && Number.isFinite(bearing) ? bearing : undefined,
      direction: direction === 'rtl' ? 'rtl' : 'ltr',
    }, obs);
    mutateScenario(s => {
      // Wipe existing auto- rows
      s.rows = s.rows.filter(r => !r.id.startsWith('auto-'));
      s.lanes = s.lanes.filter(l => !l.id.startsWith('auto-'));
      // Add new
      out.rows.forEach((r, i) => {
        s.rows.push({
          id: r.id, type: 'row', label: r.label,
          start: r.start, end: r.end,
          angle: r.opts.angle,
          slotWidth: r.opts.slotWidth,
          slotLength: r.opts.slotLength,
          side: r.opts.side,
          slantSign: r.opts.slantSign,
          facing: r.opts.facing,
          doubleSided: false,
          accessibleEvery: r.opts.accessibleEvery,
        });
      });
      out.lanes.forEach(l => s.lanes.push({ id: l.id, type: 'lane', start: l.start, end: l.end, width: l.width, direction: 'two-way' }));
    });
  }

  // ----------------------------------------------------------------
  // Layer toggles
  // ----------------------------------------------------------------
  function toggleLayer(id) { setLayers(prev => ({ ...prev, [id]: !prev[id] })); }

  // ----------------------------------------------------------------
  // Bulk-clear rows + lanes from the active scenario. Boundaries,
  // obstacles, and entries are kept. Goes through mutateScenario so
  // a single ⌘Z restores everything.
  // ----------------------------------------------------------------
  function handleClearLayout() {
    if (!hasScenario) return;
    const rowCount = scenario.rows.length;
    const laneCount = scenario.lanes.length;
    if (rowCount + laneCount === 0) return;
    const ok = window.confirm(
      `${rowCount} ${rowCount === 1 ? 'Reihe' : 'Reihen'} und ${laneCount} ${laneCount === 1 ? 'Fahrgasse' : 'Fahrgassen'} entfernen?\n\nMit ⌘Z (Strg+Z) lässt sich das rückgängig machen.`,
    );
    if (!ok) return;
    mutateScenario((s) => {
      s.rows = [];
      s.lanes = [];
    });
    setSelection(null);
  }

  // ----------------------------------------------------------------
  // Export / Import
  // ----------------------------------------------------------------
  // Row summary used by the PDF row-table. Computed lazily inside handlers
  // so we don't re-build it on every keystroke just because the modal exists.
  function buildRowSummary() {
    const map = new Map();
    for (const s of allSlots) {
      const r = scenario.rows.find((rw) => rw.id === s.rowId);
      if (!r) continue;
      let entry = map.get(r.id);
      if (!entry) {
        entry = { rowId: r.id, label: r.label, angle: r.angle, side: r.side, slots: 0, blocked: 0, accessible: 0 };
        map.set(r.id, entry);
      }
      entry.slots += 1;
      if (s.blocked) entry.blocked += 1;
      if (s.accessible) entry.accessible += 1;
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }

  // Collect all geometry points from the active scenario as [lat, lng] pairs
  // so Leaflet's LatLngBounds can consume them directly.
  function collectGeometryLatLngs(sc) {
    const out = [];
    for (const b of sc.boundaries || []) for (const [lng, lat] of b.polygon || []) out.push([lat, lng]);
    for (const o of sc.obstacles  || []) for (const [lng, lat] of o.polygon || []) out.push([lat, lng]);
    for (const r of sc.rows       || []) {
      if (r.start) out.push([r.start[1], r.start[0]]);
      if (r.end)   out.push([r.end[1],   r.end[0]]);
    }
    for (const l of sc.lanes      || []) {
      if (l.start) out.push([l.start[1], l.start[0]]);
      if (l.end)   out.push([l.end[1],   l.end[0]]);
    }
    for (const e of sc.entries    || []) {
      if (e.position) out.push([e.position[1], e.position[0]]);
    }
    return out;
  }

  // Fit the map to all geometry, wait for tiles, snapshot, then restore the
  // prior view. If there's no geometry yet, snapshot the current view as-is.
  async function snapshotFittedToGeometry() {
    const map = mapRef.current;
    if (!map || !mapEl.current) throw new Error('Karte ist nicht bereit.');

    const pts = collectGeometryLatLngs(scenario);
    const prevCenter = map.getCenter();
    const prevZoom = map.getZoom();
    let restore = false;

    if (pts.length >= 2) {
      const bounds = L.latLngBounds(pts);
      // Generous padding so labels at the edge aren't clipped.
      map.fitBounds(bounds, { padding: [40, 40], animate: false, maxZoom: 19 });
      restore = true;
    } else if (pts.length === 1) {
      map.setView(pts[0], 19, { animate: false });
      restore = true;
    }

    // Wait for tiles to settle. Esri tiles fire `load` on the tile layer once
    // every visible tile is loaded; bail out after 4s as a safety net.
    await new Promise((resolve) => {
      const tile = tileRef.current;
      if (!tile) return resolve();
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        tile.off('load', finish);
        resolve();
      };
      // If the layer reports no pending tiles already, resolve next tick.
      if (tile._loading === false) return setTimeout(finish, 50);
      tile.on('load', finish);
      setTimeout(finish, 4000);
    });

    // Give Leaflet one more frame so vector overlays redraw at the new view.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    // Geometry bbox in container-pixel space. Needs to be read BEFORE we
    // restore the previous view (otherwise points wouldn't map correctly).
    let bboxPx = null;
    if (pts.length >= 1) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const [lat, lng] of pts) {
        const p = map.latLngToContainerPoint([lat, lng]);
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
      }
      bboxPx = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }

    // Waypoint pixel positions for the PDF overlay. One pre-cropped list;
    // the PDF stage applies the same crop transform that's used on the image.
    const waypointsPx = [];
    for (const r of scenario.rows || []) {
      const wps = sampleWaypoints(r.start, r.end, 10);
      wps.forEach((wp, idx) => {
        const p = map.latLngToContainerPoint([wp.lat, wp.lng]);
        waypointsPx.push({
          kind: 'row',
          parentLabel: r.label || '',
          idx: idx + 1,
          x: p.x,
          y: p.y,
        });
      });
    }
    for (const l of scenario.lanes || []) {
      const wps = sampleWaypoints(l.start, l.end, 10);
      wps.forEach((wp, idx) => {
        const p = map.latLngToContainerPoint([wp.lat, wp.lng]);
        waypointsPx.push({
          kind: 'lane',
          parentLabel: String((scenario.lanes || []).indexOf(l) + 1),
          idx: idx + 1,
          x: p.x,
          y: p.y,
        });
      });
    }

    let snap;
    try {
      snap = await snapshotMapPng(mapEl.current);
    } finally {
      if (restore) {
        map.setView(prevCenter, prevZoom, { animate: false });
      }
    }
    return { ...snap, bboxPx, waypointsPx };
  }

  async function handleExportPdf() {
    if (!hasScenario) throw new Error('Kein Szenario aktiv.');
    let mapSnapshot = null;
    let pageOneMap = null;
    try {
      const raw = await snapshotFittedToGeometry();
      // Stamp numbered waypoint dots onto the snapshot BEFORE cropping, so
      // the same dots travel through to both the cropped page-1 image and
      // the full landscape Lageplan.
      try {
        mapSnapshot = await overlayWaypoints(raw, mapEl.current);
      } catch (oErr) {
        console.warn('Waypoint overlay failed, using plain snapshot:', oErr);
        mapSnapshot = raw;
      }
      // Page 1 is cropped to just the parking area (with breathing room) so
      // the print isn't dominated by surrounding map.
      try {
        pageOneMap = await cropToBbox(mapSnapshot, mapEl.current);
      } catch (cErr) {
        console.warn('Crop failed, using full snapshot on page 1:', cErr);
        pageOneMap = mapSnapshot;
      }
    } catch (e) {
      // Snapshot failure shouldn't block the PDF — emit a console warning and
      // continue with a text-only first page.
      console.warn('Map snapshot failed, continuing without image:', e);
    }
    downloadPdf({
      scenario,
      slots: allSlots,
      rowSummary: buildRowSummary(),
      mapSnapshot,
      pageOneMap,
    });
  }

  async function handleExportPng() {
    if (!hasScenario) throw new Error('Kein Szenario aktiv.');
    const snap = await snapshotFittedToGeometry();
    downloadPng(snap, scenario);
  }

  function handleExportJson() {
    if (!hasScenario) throw new Error('Kein Szenario aktiv.');
    downloadJson(scenario);
  }

  async function handleImportJson(file) {
    const sc = await importJsonFile(file);
    setScenarios((prev) => ({ ...prev, [sc.id]: sc }));
    setHistory((h) => ({ ...h, [sc.id]: { past: [], future: [] } }));
    setScenarioId(sc.id);
  }

  // ----------------------------------------------------------------
  // Keyboard shortcuts
  // ----------------------------------------------------------------
  useEffect(() => {
    const handler = (e) => {
      // ignore when typing in inputs
      const t = e.target;
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable) return;

      if (e.key === 'Escape') {
        if (drawing) setDrawing(null);
        else if (modal) setModal(null);
        else setSelection(null);
        return;
      }
      if (e.key === 'Enter') {
        if (drawing && (drawing.type === 'boundary' || drawing.type === 'obstacle') && drawing.points.length >= 3) {
          handleFinishPolygon();
        }
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selection) {
          deleteObject(selection.type, selection.id);
        }
        return;
      }

      // Undo / Redo — ⌘Z / Ctrl+Z, ⇧⌘Z / Ctrl+Shift+Z (also Ctrl+Y)
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (mod && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        redo();
        return;
      }

      const shortcuts = {
        'v': 'select', 'h': 'pan',
        'b': 'boundary', 'r': 'row', 'f': 'lane', 'o': 'obstacle',
        'm': 'measure', 'e': 'entry', 'c': 'coords',
      };
      const t2 = shortcuts[e.key.toLowerCase()];
      if (t2) { setTool(t2); setDrawing(null); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <div className="app-shell" data-screen-label="01 Main editor">
      <Topbar
        scenarios={scenarios}
        scenario={scenario}
        scenarioId={scenarioId}
        hasScenario={hasScenario}
        onSwitchScenario={setScenarioId}
        onNewScenario={() => setModal({ kind: 'address', mode: 'create' })}
        onChangeAddress={() => setModal({ kind: 'address', mode: 'change', initialQuery: scenario.address || '' })}
        onDeleteScenario={deleteCurrentScenario}
        onOpenSettings={() => setModal({ kind: 'settings' })}
        onOpenExport={() => setModal({ kind: 'export' })}
        onOpenAutofill={() => setModal({ kind: 'autofill' })}
        onClearLayout={handleClearLayout}
        onImportJson={() => { setImportError(null); fileInputRef.current?.click(); }}
        onUndo={undo} onRedo={redo} canUndo={canUndo} canRedo={canRedo}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = ''; // reset so the same file can be re-picked
          if (!file) return;
          try {
            await handleImportJson(file);
          } catch (err) {
            setImportError(err.message || 'Import fehlgeschlagen.');
            setTimeout(() => setImportError(null), 5000);
          }
        }}
      />

      <Leftbar tool={tool} onSelectTool={(t) => { setTool(t); setDrawing(null); }} />

      <div className={`mapcanvas tool-${bearingPickActive ? 'coords' : tool}`}>
        <div id="map" ref={mapEl} />
        {importError && (
          <div className="draw-hint" style={{ borderColor: 'var(--danger)' }}>
            <span style={{ fontWeight: 600, color: 'var(--danger)' }}>Import-Fehler</span>
            <span style={{ color: 'var(--text-2)' }}>· {importError}</span>
          </div>
        )}
        {bearingPickActive && (
          <div className="draw-hint">
            <Icon name="compass" size={14} />
            <span style={{ fontWeight: 600 }}>Reihenrichtung wählen</span>
            <span style={{ color: 'var(--text-2)' }}>
              · {bearingPickRef.current?.points.length === 0 ? 'Setze den ersten Punkt' : 'Setze den zweiten Punkt'}
            </span>
            <button
              className="btn"
              style={{ height: 22, padding: '0 8px', fontSize: 11 }}
              onClick={() => {
                bearingPickRef.current = null;
                setBearingPickActive(false);
                setModal({ kind: 'autofill', boundaryId: undefined });
              }}
            >
              Abbrechen
            </button>
          </div>
        )}
        {hasScenario && <HUD counts={counts} target={target} scenario={scenario} blockedCount={blockedCount} />}
        {!hasScenario && <WelcomeOverlay onPickAddress={() => setModal({ kind: 'address', mode: 'create' })} />}
        {drawing && (
          <DrawHint
            tool={tool}
            drawing={drawing}
            onFinish={handleFinishPolygon}
            onCancel={() => setDrawing(null)}
          />
        )}
        <LayersCard layers={layers} onToggle={toggleLayer} />
        <Compass />
        <StatusBar tool={tool} mouseCoord={mouseCoord} mapZoom={mapZoom} scenario={scenario} drawing={drawing} />
      </div>

      <Inspector
        selection={selection}
        scenario={scenario}
        onUpdateRow={updateRow}
        onDeleteObject={deleteObject}
        onAddRowToBoundary={addRowsToBoundary}
      />

      {/* Tweaks panel */}
      <ParkingTweaks
        tweaks={tweaks}
        setTweak={setTweak}
        target={target}
      />

      {/* Modals */}
      {modal?.kind === 'address' && (
        <AddressModal
          mode={modal.mode || 'create'}
          initialQuery={modal.initialQuery || ''}
          onClose={() => {
            // Allow dismissing only if a scenario already exists.
            if (hasScenario) setModal(null);
          }}
          onPick={(hit) => {
            if (modal.mode === 'change') {
              changeScenarioAddress(hit);
              setModal(null);
            } else {
              // Step 1 of new-project: park the address, open settings step
              // pre-filled with the user's last-used defaults.
              setPendingAddress(hit);
              setPendingSettings({ ...DEFAULT_SETTINGS, ...lastSettings });
              setModal({ kind: 'project-settings' });
            }
          }}
        />
      )}
      {modal?.kind === 'project-settings' && pendingAddress && pendingSettings && (
        <SettingsModal
          mode="create"
          addressLabel={pendingAddress.address || pendingAddress.name}
          settings={pendingSettings}
          onChange={(patch) => setPendingSettings((prev) => ({ ...prev, ...patch }))}
          onBack={() => {
            setModal({ kind: 'address', mode: 'create', initialQuery: pendingAddress?.address || pendingAddress?.name || '' });
          }}
          onConfirm={() => {
            createScenarioFromAddress({ ...pendingAddress, settings: pendingSettings });
            setLastSettings(pendingSettings);
            setPendingAddress(null);
            setPendingSettings(null);
            setModal(null);
          }}
          onClose={() => {
            // X / backdrop click: also treat as cancel.
            setPendingAddress(null);
            setPendingSettings(null);
            if (hasScenario) setModal(null);
            else setModal({ kind: 'address', mode: 'create' });
          }}
        />
      )}
      {modal?.kind === 'settings' && (
        <SettingsModal
          settings={settings}
          onChange={(patch) => {
            if (hasScenario) {
              mutateScenario((s) => {
                s.settings = { ...(s.settings || DEFAULT_SETTINGS), ...patch };
              });
              // Mirror into lastSettings so the next new scenario inherits these.
              setLastSettings((ls) => ({ ...ls, ...patch }));
            }
          }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'export' && (
        <PdfExportModal
          scenario={scenario}
          slots={allSlots}
          onClose={() => setModal(null)}
          onExportPdf={handleExportPdf}
          onExportPng={handleExportPng}
          onExportJson={handleExportJson}
          getMapSnapshot={async () => {
            // Same pipeline as the real export so preview matches.
            const raw = await snapshotFittedToGeometry();
            try { return await overlayWaypoints(raw, mapEl.current); }
            catch { return raw; }
          }}
          getPageOneSnapshot={(snap) => cropToBbox(snap, mapEl.current)}
        />
      )}
      {modal?.kind === 'autofill' && (
        <AutofillModal
          scenario={scenario}
          initialBearing={modal.bearing ?? null}
          onClose={() => setModal(null)}
          onApply={(cfg) => applyAutofill(cfg, modal.boundaryId)}
          onPickBearing={() => {
            const boundaryId = modal.boundaryId;
            // Stash the modal so we can reopen it after the user picks.
            setModal(null);
            bearingPickRef.current = {
              points: [],
              onDone: (deg) => {
                setModal({ kind: 'autofill', boundaryId, bearing: deg });
              },
            };
            setBearingPickActive(true);
          }}
        />
      )}
    </div>
  );
}

// =============================================================
// Tweaks panel
// =============================================================
function ParkingTweaks({ tweaks, setTweak, target }) {
  return (
    <TweaksPanel title="Tweaks">
      <TweakSection label="Globaler Winkel" />
      <TweakToggle
        label="Auf alle Reihen anwenden"
        value={tweaks.applyToAllRows}
        onChange={(v) => setTweak('applyToAllRows', v)}
      />
      <TweakRadio
        label="Winkel"
        options={[
          { value: 0,  label: '0°' },
          { value: 45, label: '45°' },
          { value: 60, label: '60°' },
          { value: 75, label: '75°' },
          { value: 90, label: '90°' },
        ]}
        value={tweaks.globalAngle}
        onChange={(v) => setTweak('globalAngle', v)}
      />
      <TweakToggle
        label="Beidseitige Reihen"
        value={tweaks.doubleSided}
        onChange={(v) => setTweak('doubleSided', v)}
      />

      <TweakSection label="Slot-Maße" />
      <TweakSlider
        label="Breite"
        value={tweaks.slotWidth}
        min={2.0} max={3.5} step={0.1}
        unit=" m"
        onChange={(v) => setTweak('slotWidth', v)}
      />
      <TweakSlider
        label="Länge"
        value={tweaks.slotLength}
        min={3.5} max={7.0} step={0.1}
        unit=" m"
        onChange={(v) => setTweak('slotLength', v)}
      />

      <TweakSection label="Ziel" />
      <TweakSlider
        label="Kapazitäts-Ziel"
        value={tweaks.target}
        min={0} max={500} step={10}
        unit=" Plätze"
        onChange={(v) => setTweak('target', v)}
      />

      <TweakSection label="Darstellung" />
      <TweakToggle
        label="Blockierte Lücken markieren"
        value={tweaks.highlightBlocked}
        onChange={(v) => setTweak('highlightBlocked', v)}
      />
      <TweakToggle
        label="Winkelpfeil je Reihe"
        value={tweaks.showAngleArrow}
        onChange={(v) => setTweak('showAngleArrow', v)}
      />
    </TweaksPanel>
  );
}

